import {
  annotationMarksOf,
  annotationRoom,
  chartTableOf,
  dailyBars,
  dailyTrend,
  markWidthOf,
  summaryOf,
  tooltipOf,
  type AnnotationMark,
  type ChartDay,
  type ChartPaint,
  type ChartSeries,
  type DailyChartInput,
} from "@beacon/core/charts"
import { dayLabel } from "@beacon/core/days"
import { defineChart, type ChartScene } from "@tanstack/charts"
import { tooltip } from "@tanstack/charts/tooltip"
import { portal } from "@tanstack/charts/tooltip/portal"
import { Chart } from "@tanstack/react-charts"
import { useCallback, useId, useMemo, useRef, useState, type ReactNode } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/primitives/table.tsx"
import { cn } from "@/primitives/utils"
import { screenPaints, tellMarkOf, tooltipAnchorOf } from "./daily-chart.ts"
import { useLadder } from "./page-chrome.tsx"
import { useViewport } from "./viewport.tsx"

/*
 * A range of UTC days drawn as TanStack Charts: stacked bars (`bars`) or one line a series
 * (`trend`). The definition is `@beacon/core/charts`', which a PDF prints too; this composite
 * mounts it with the tokens as its paints and gives it what only a screen has. Over the chart: a
 * legend, each series' swatch and its name, and "History not known" whenever a day is. On it: a
 * day's tooltip on hover or keyboard focus (pinned by a tap on touch, which has no hover, unless
 * the page tells the picked day itself), a day picked by a click, a tap, Enter or Space, the other
 * days' bars receding behind it, and each mark over the plot as a ring that opens its annotations'
 * detail. On `compact`, marks too close to stand apart stand as one. Under it: the same data as a
 * table, shown on asking, and the chart in a sentence for a screen reader. The keyboard reaches
 * everything the pointer does.
 */

/** A note on a day: its label on the chart, what it is called, and what its popover tells. */
export type DailyChartAnnotation = {
  day: string
  label: string
  /** What a screen reader calls the button: "Agent model change 1". */
  about: string
  detail: ReactNode
}

const heights = { short: 192, tall: 288 } as const

/** Half a day's tooltip's width, in px: how far either side of its day it covers the plot. */
const tooltipReach = 80

/** How tall a mark's ring is, inside the room above the plot. */
const ringHeight = 16

/** How far a mark's button reaches about its ring on touch: a finger's 44px. */
const touchReach = 44

/** None, as one array, so a chart without annotations isn't rebuilt on every render. */
const noAnnotations: readonly never[] = []

/** Where the chart laid itself out, read from its scene after every layout. */
type Placed = { top: number; width: number; at: Record<string, number> }

export function DailyChart<K extends string, A extends DailyChartAnnotation>({
  kind,
  label,
  series,
  days,
  unit,
  formatValue,
  valueTicks,
  annotations = noAnnotations,
  annotationsName,
  annotationPaint,
  tellMark = tellMarkOf,
  picked,
  onPick,
  touchTooltip = true,
  height = "tall",
}: {
  kind: "bars" | "trend"
  /** What the chart shows, as a screen reader names it and its table is captioned. */
  label: string
  series: readonly ChartSeries<K>[]
  /** Every day of the range in order; a day whose history isn't known has null values. */
  days: readonly ChartDay<K>[]
  /** What the values count, as the value axis is titled: "Tickets". */
  unit: string
  formatValue?: (value: number) => string
  /** The value axis's own ticks and how they are written, as money's share one precision. */
  valueTicks?: DailyChartInput<K>["valueTicks"]
  /** One a thing noted; several may share a day, whose mark then names them all. */
  annotations?: readonly A[]
  /** What the annotations are, as an annotated day's tooltip names them: "Agent model changed". */
  annotationsName?: string
  annotationPaint?: ChartPaint
  /**
   * What a mark's button is called and its popover tells, from the annotations it stands for. One
   * annotation's own `about` and `detail` unless set; set it to tell several as one.
   */
  tellMark?: (mark: AnnotationMark<A>) => { about: string; detail: ReactNode }
  picked?: string | null
  onPick?: (day: string | null) => void
  /** Whether a tap on touch pins the day's tooltip; off where the page tells the picked day. */
  touchTooltip?: boolean
  height?: keyof typeof heights
}) {
  const { compact, touch } = useViewport()
  const [placed, setPlaced] = useState<Placed>()
  // On `compact` the plot's width decides which marks have room to stand apart; on any screen, how
  // far apart the day axis's labels stand.
  const plotWidth = compact ? placed?.width : undefined
  const axisWidth = placed?.width
  // Whether a mark's popover is open as a press lands: on touch, a tap that only closes it picks
  // no day.
  const openMarks = useRef(new Set<string>())
  const dismissing = useRef(false)
  const input: DailyChartInput<K> & { annotations: readonly A[] } = useMemo(
    () => ({
      series,
      days,
      unit,
      paints: screenPaints,
      annotations,
      picked,
      onPick,
      ...(formatValue && { formatValue }),
      ...(valueTicks && { valueTicks }),
      ...(annotationsName && { annotationsName }),
      ...(annotationPaint && { annotationPaint }),
      ...(plotWidth !== undefined && { plotWidth }),
      ...(axisWidth !== undefined && { axisWidth }),
    }),
    [
      series,
      days,
      unit,
      annotations,
      picked,
      onPick,
      formatValue,
      valueTicks,
      annotationsName,
      annotationPaint,
      plotWidth,
      axisWidth,
    ],
  )
  const host = useRef<HTMLDivElement>(null)
  const { stuck } = useLadder()
  const definition = useMemo(() => {
    const base = kind === "bars" ? dailyBars(input) : dailyTrend(input)
    // On touch with the page telling the picked day, a tap only picks it.
    if (touch && !touchTooltip) return base
    // How far down the chart the page's sticky chrome reaches as it scrolls under it, and room
    // under that for the tooltip to stand over its anchor (a row a series, the title and Total)
    // on the desk, or a gap to hang under it on `compact`.
    const room = compact ? 8 : 44 + 16 * (series.length + 2)
    const clearFrom = () => {
      const chart = host.current
      const scroller = chart?.closest('[data-slot="page-scroll"]')
      if (!chart || !scroller) return 0
      return scroller.getBoundingClientRect().top + stuck - chart.getBoundingClientRect().top + room
    }
    // Each day's top as drawn (its stack's, or its highest line's), for the desk's tooltip to
    // stand over the neighbours it would otherwise cover.
    const topsOf = (scales: Readonly<Record<string, { map: (value: unknown) => number }>>) => {
      const { x, y } = scales
      if (!x || !y) return []
      return input.days.flatMap(({ day, values }) => {
        if (!values) return []
        const amounts = input.series.map(({ key }) => values[key])
        const top =
          kind === "bars" ? amounts.reduce((sum, amount) => sum + amount, 0) : Math.max(...amounts)
        return top > 0 ? [{ x: x.map(day), y: y.map(top) }] : []
      })
    }
    return defineChart(base, {
      tooltip: {
        use: tooltip,
        portal,
        content: tooltipOf(input),
        // On `compact` the tooltip hangs from the plot's top, inside the chart, rather than rising
        // over the card's header; on the desk it stands over the day's stack, and over any taller
        // neighbour it would cover. Either way it stays clear of the sticky chrome a scrolled
        // chart runs under.
        anchor: (points, { plot, scales }) =>
          tooltipAnchorOf({
            points,
            plot,
            clearFrom: clearFrom(),
            compact,
            tops: topsOf(scales),
            reach: tooltipReach,
          }),
        placement: compact
          ? (["bottom", "bottom-left", "bottom-right"] as const)
          : (["top", "right", "left", "bottom"] as const),
        // Touch has no hover: a tap picks the day and pins its tooltip until a tap elsewhere. A
        // pointer's tooltip follows it and goes with it, a click's pick included.
        visibility: touch ? "pinned" : "focus",
        sticky: touch,
      },
    })
  }, [kind, input, touch, touchTooltip, compact, series.length, stuck])
  const description = useMemo(() => summaryOf(input), [input])
  const marks = useMemo(() => annotationMarksOf(input), [input])
  const onRender = useCallback(
    ({ scene }: { scene: Pick<ChartScene, "chart" | "scales"> }) => {
      const x = scene.scales.x
      if (!x) return
      const at = Object.fromEntries(annotations.map(({ day }) => [day, x.map(day)]))
      const next = { top: scene.chart.y, width: scene.chart.width, at }
      setPlaced((now) => (JSON.stringify(now) === JSON.stringify(next) ? now : next))
    },
    [annotations],
  )
  const unknown = days.some(({ values }) => values === null)
  // A single series is what the card's title already names: its legend only stands beside the
  // history not known, to tell the two apart.
  const legend = series.length > 1 || unknown
  const pickedDay = picked && days.some(({ day }) => day === picked) ? picked : undefined
  const paint = screenPaints(annotationPaint ?? "muted-foreground")
  return (
    <figure className="space-y-3">
      {legend && (
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {series.map(({ key, label, paint }) => (
            <li key={key} className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden
                className="size-2 rounded-full"
                style={{ backgroundColor: screenPaints(paint) }}
              />
              {label}
            </li>
          ))}
          {unknown && (
            <li className="flex items-center gap-1.5 whitespace-nowrap">
              <span aria-hidden className="size-2 rounded-xs bg-muted ring-1 ring-border" />
              History not known
            </li>
          )}
        </ul>
      )}
      <div className="relative">
        <div
          ref={host}
          onPointerDownCapture={() => {
            dismissing.current = touch && openMarks.current.size > 0
          }}
          onClickCapture={(event) => {
            // The popover closes on the tap; the chart under it never hears of it.
            if (dismissing.current) event.stopPropagation()
            dismissing.current = false
          }}
        >
          <Chart
            definition={definition}
            height={heights[height]}
            ariaLabel={label}
            ariaDescription={description}
            className="daily-chart text-xs text-muted-foreground"
            onRender={onRender}
          />
        </div>
        {placed &&
          marks.map((mark) => {
            const left = placed.at[mark.day] ?? 0
            return (
              <MarkButton
                key={mark.day}
                mark={mark}
                told={tellMark(mark)}
                paint={paint}
                left={left}
                top={Math.max(0, placed.top - annotationRoom)}
                touch={touch}
                // A popover grows towards the plot's middle, so it stays over the chart.
                align={left > placed.width / 2 ? "end" : "start"}
                onOpenChange={(open) => {
                  if (open) openMarks.current.add(mark.day)
                  else openMarks.current.delete(mark.day)
                }}
              />
            )
          })}
      </div>
      <p className="sr-only" aria-live="polite">
        {pickedDay ? `${dayLabel(pickedDay)} picked` : ""}
      </p>
      <DataTable input={input} label={label} />
    </figure>
  )
}

/**
 * A mark's button, over the label the chart draws: a ring in the annotations' paint about the
 * label, and, on touch, a finger's 44px. Hovered, focused or tapped, it tells its detail in a
 * popover anchored at it.
 */
function MarkButton({
  mark,
  told,
  paint,
  left,
  top,
  touch,
  align,
  onOpenChange,
}: {
  mark: AnnotationMark
  told: { about: string; detail: ReactNode }
  paint: string
  left: number
  top: number
  touch: boolean
  align: "start" | "end"
  onOpenChange: (open: boolean) => void
}) {
  const ring = markWidthOf(mark.label)
  const width = touch ? Math.max(ring, touchReach) : ring
  const height = touch ? touchReach : annotationRoom
  return (
    // Read in place at a glance, on a hover as much as a tap, so it stays anchored on a phone too.
    <Popover anchored onOpenChange={onOpenChange}>
      <PopoverTrigger
        openOnHover
        delay={100}
        render={
          <button
            type="button"
            aria-label={told.about}
            className="group/mark absolute flex -translate-x-1/2 items-center justify-center outline-none"
            style={{ left, top: top - (height - annotationRoom) / 2, width, height }}
          >
            <span
              aria-hidden
              className="rounded-full border group-focus-visible/mark:ring-2 group-focus-visible/mark:ring-ring"
              // The ring takes the paint the chart draws its label and lines in.
              style={{ width: ring, height: ringHeight, borderColor: paint }}
            />
          </button>
        }
      />
      <PopoverContent
        side="top"
        align={align}
        // 16px clear of the screen's edge, and kept above the mark, shifted rather than flipped
        // under it where there's no room, so it never covers the bars it notes.
        collisionPadding={16}
        collisionAvoidance={{ side: "shift", align: "shift" }}
        className="w-max max-w-xl-available"
      >
        {told.detail}
      </PopoverContent>
    </Popover>
  )
}

/** The chart's data as a table, shown on asking, a run of days not known saying so in one row. */
function DataTable<K extends string>({
  input,
  label,
}: {
  input: DailyChartInput<K>
  label: string
}) {
  const [shown, setShown] = useState(false)
  const id = useId()
  const table = useMemo(() => chartTableOf(input), [input])
  // Which cells of each known day are of nothing, its day too when all are, so they recede and the
  // days with figures stand out.
  const zeros = useMemo(() => {
    const keys = input.series.map(({ key }) => key)
    return new Map(
      input.days.flatMap(({ day, values }) => {
        if (!values) return []
        const each = keys.map((key) => values[key] === 0)
        const none = each.every(Boolean)
        return [[day, [none, ...each, none]] as const]
      }),
    )
  }, [input])
  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-expanded={shown}
        aria-controls={id}
        onClick={() => setShown(!shown)}
        className="touch-target text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {shown ? "Hide the data" : "Show the data"}
      </button>
      {shown && (
        // On `compact` the table flows with the page rather than scrolling inside it.
        <div
          id={id}
          className="max-h-72 overflow-auto rounded-md ring-1 ring-border-subtle compact:max-h-none"
        >
          <Table>
            <TableCaption className="sr-only">{label}</TableCaption>
            <TableHeader>
              <TableRow>
                {table.columns.map((column, index) => (
                  <TableHead key={column} className={cn(index > 0 && "text-right")}>
                    {column}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.rows.map(({ day, known, cells }) => (
                // A run of days not known is one muted row, so the figures stand out.
                <TableRow key={day} className={cn(!known && "text-muted-foreground")}>
                  {cells.map((cell, index) => (
                    <TableCell
                      key={table.columns[index]}
                      className={cn(
                        index > 0 && "text-right tabular-nums",
                        zeros.get(day)?.[index] && "text-subtle-foreground",
                      )}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
